import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { UserService } from '../services/user.service';
import { RouterService } from '../services/router.services';
import { LoadingService } from '@awo-care/shared-core';

@Injectable({
  providedIn: 'root',
})
export class ClaimGuard {
  private router = inject(Router);
  private userService = inject(UserService);
  private routerService = inject(RouterService);
  private loadingService = inject(LoadingService);

  async canActivate(next: ActivatedRouteSnapshot): Promise<boolean> {
    try {
      this.loadingService.setLoading(true);
      // Wait for initialization
      if (!this.userService.isInitialized) {
        console.debug('Waiting for UserService initialization...');
        await this.userService.loadClaims();
      }

      const user = this.userService.currentUser();

      // Check if user exists and is authenticated
      if (!user?.uid) {
        await this.router.navigate(['auth/login'], {
          queryParams: { returnUrl: next.url.join('/') },
        });
        return false;
      }

      // Get required claims from route data
      const requiredClaims = next.data['requiredClaims'] as
        | string[]
        | undefined;
      if (!requiredClaims?.length) {
        return true;
      }

      // Check if user has any of the required claims
      const hasAnyRequiredClaim = requiredClaims.some((claim) =>
        this.userService.hasClaim(claim)
      );

      const teamleadExcluded =
        next.data['excludeTeamlead'] === true && this.userService.isTeamlead();

      if (!hasAnyRequiredClaim || teamleadExcluded) {
        console.warn(
          'Access denied: User lacks required claims:',
          requiredClaims.join(', ')
        );
        await this.routerService.navigateToDefaultRoute();
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in ClaimGuard:', error);
      await this.router.navigate(['auth/login']);
      return false;
    } finally {
      this.loadingService.setLoading(false);
    }
  }
}
